import { nextTick } from 'vue'
import { gsap } from 'gsap'

const ENTER_Y = 24
const HAND_NUDGE = 8
const HAND_CUE_DURATION = 2.4
const HAND_REPEAT_DELAY = 8
const LOADER_WAIT_FRAMES = 30

const isReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches
const waitFrame = () => new Promise((resolve) => requestAnimationFrame(resolve))

export function createFcknRowMotion({
	el,
	leftYear,
	leftLogo,
	preorderButton,
	rightLabel,
	leftHand,
	rightHand,
	getLoader,
}) {
	let ctx = null
	let enterTimeline = null
	let handTimeline = null
	let isSetup = false

	const getMainItems = () =>
		[
			leftYear?.value,
			leftLogo?.value,
			preorderButton.value,
			rightLabel.value,
		].filter(Boolean)
	const getHands = () => [leftHand.value, rightHand.value].filter(Boolean)

	const setup = async () => {
		if (!import.meta.client || !el.value) return

		await nextTick()
		if (!el.value) return

		const mainItems = getMainItems()
		const hands = getHands()

		ctx = gsap.context(() => {
			gsap.killTweensOf([...mainItems, ...hands])

			if (isReducedMotion()) {
				mainItems.length && gsap.set(mainItems, { clearProps: 'transform,opacity,visibility' })
				hands.length && gsap.set(hands, { autoAlpha: 0, x: 0 })
				return
			}

			mainItems.length && gsap.set(mainItems, { y: ENTER_Y, autoAlpha: 0 })
			hands.length && gsap.set(hands, { x: 0, autoAlpha: 0 })
			isSetup = true
		}, el.value)
	}

	const init = async () => {
		if (!import.meta.client || !el.value || isReducedMotion()) return

		await nextTick()
		if (!el.value) return

		if (!isSetup) {
			await setup()
		}

		const mainItems = getMainItems()
		const hands = getHands()

		ctx?.add(() => {
			gsap.killTweensOf([...mainItems, ...hands])

			if (hands.length === 2) {
				handTimeline = gsap.timeline({
					paused: true,
					repeat: -1,
					repeatDelay: HAND_REPEAT_DELAY,
				})

				handTimeline
					.to(hands, { autoAlpha: 1, duration: 0.24, ease: 'power2.out' }, 0)
					.to(
						leftHand.value,
						{ x: HAND_NUDGE, duration: 0.32, ease: 'sine.inOut', repeat: 5, yoyo: true },
						0.16,
					)
					.to(
						rightHand.value,
						{ x: -HAND_NUDGE, duration: 0.32, ease: 'sine.inOut', repeat: 5, yoyo: true },
						0.16,
					)
					.to(hands, { autoAlpha: 0, duration: 0.28, ease: 'power2.in' }, HAND_CUE_DURATION)
			}

			enterTimeline = gsap.timeline()
			enterTimeline
				.to(mainItems, {
					y: 0,
					autoAlpha: 1,
					duration: 0.65,
					stagger: 0.07,
					ease: 'power3.out',
				})
				.add(() => handTimeline?.play(0), '>-0.05')
		})
	}

	const mount = async () => {
		if (!import.meta.client) return

		await nextTick()

		let loader = getLoader?.()

		for (let i = 0; !loader && i < LOADER_WAIT_FRAMES; i += 1) {
			await waitFrame()
			loader = getLoader?.()
		}

		if (!loader) {
			await setup()
			await init()
			return
		}

		loader.loaded.then(() => setup())
		loader.ready.then(() => init())
	}

	const kill = () => {
		enterTimeline?.kill()
		handTimeline?.kill()
		ctx?.revert()

		enterTimeline = null
		handTimeline = null
		ctx = null
		isSetup = false
	}

	return {
		mount,
		kill,
	}
}
